import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resent, setResent] = useState(false);
  const navigate = useNavigate();
  const { login, needsVerification, resendVerification, sendPasswordReset } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResent(false);
    const err = await login(email, password);
    if (err) {
      setError(err);
      return;
    }
    navigate('/');
  };

  const handleResend = async () => {
    setError('');
    setResent(false);
    const err = await resendVerification();
    if (err) setError(err);
    else setResent(true);
  };

  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [showReset, setShowReset] = useState(false);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetSent(false);
    const err = await sendPasswordReset(resetEmail, 'https://just-vibes-guild.com/login');
    // Always show the generic message for privacy, even if err
    setResetSent(true);
    if (err) {
      setResetError(err);
      console.error('Password reset error:', err);
    }
  };

  return (
    <div className="page-container flex items-center justify-center bg-surface">
      <div className="w-full max-w-md">
        <form onSubmit={handleLogin} className="glass p-8 rounded-xl w-full space-y-6">
          <h1 className="text-3xl font-bold text-center mb-4 gradient-text">Login</h1>
          {error && <div className="text-red-400 text-center">{error}</div>}
          {needsVerification && (
            <div className="text-yellow-400 text-center font-semibold py-4">
              Please verify your email address before logging in.<br />
              <button
                type="button"
                className="mt-2 btn-neon px-4 py-1 rounded font-bold"
                onClick={handleResend}
                disabled={resent}
              >
                {resent ? 'Verification Email Sent!' : 'Resend Verification Email'}
              </button>
            </div>
          )}
          <input
            type="email"
            placeholder="Email"
            className="w-full p-3 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            className="w-full p-3 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <div className="text-right text-sm mt-1">
            <button type="button" className="text-cyan-400 hover:underline" onClick={() => setShowReset(true)}>
              Forgot password?
            </button>
          </div>
          <button type="submit" className="w-full btn-neon py-2 rounded font-bold">Login</button>
          <div className="text-center text-text-secondary mt-4">
            Don't have an account? <Link to="/register" className="text-cyan-400 hover:underline">Create Account</Link>
          </div>
        </form>
        {showReset && (
          <form onSubmit={handlePasswordReset} className="mt-4 glass p-6 rounded-xl space-y-2">
            <h2 className="text-xl font-bold text-center mb-2 gradient-text">Reset Password</h2>
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              required
            />
            <button type="submit" className="w-full btn-neon py-1 rounded font-bold">Send Reset Link</button>
            {resetSent && (
              <div className="text-green-400 text-center">
                If an account exists for this email, a password reset link has been sent.<br />
                Please check your inbox and spam folder.
              </div>
            )}
            {resetError && <div className="text-red-400 text-center">{resetError}</div>}
            <div className="text-center mt-2">
              <button type="button" className="text-cyan-400 hover:underline" onClick={() => setShowReset(false)}>
                Back to Login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login; 